import { PortableText } from "@portabletext/react";
import { Button, DatePicker, DialogFooter, ErrorContainer, Form, FormCheckbox, FormControl, FormField, FormInput, FormItem, FormLabel, FormMessage, Heading, ScrollArea, Text } from "@sentricmusic/ui";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetMyWriters } from "@/api/queries";
import useSiteConfigAuth from "@/api/sanity/queries/getSiteConfigWithCode";
import useWritersTerms from "@/api/sanity/queries/getWritersTerms";
import { sendProperty } from "@/helpers/helpers";
import useAppStore from "@/store/appStateZustand";
import { useUIStateStore } from "@/store/uiStateStore";

import { useImroWriterForm } from "./hooks/useImroWriterForm";
import { useWriterMutation } from "./hooks/useWriterMutation";

/**
 * @typedef {object} ImroWriterForm
 * @property {() => void} onClose - Callback to close the IMRO writer registration form.
 * @property {(writerData: any) => void} [onSuccess] - Optional callback invoked when the IMRO writer registration succeeds.
 * @property {() => void} [onBack] - Optional callback to go back to the standard writer form.
 * @property {object} [initialData] - Optional initial form data (firstname, lastname, middlename, etc.) carried over from standard writer form.
 */

export default function ImroWriterForm({ onClose, onSuccess, onBack, initialData }) {
  const { t } = useTranslation(["writers", "common"]);
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const selectedAccount = useAppStore(s => s.selectedAccount);

  // State for API validation errors
  const [validationErrors, setValidationErrors] = useState([]);
  const [generalError, setGeneralError] = useState("");

  // State for Terms & Conditions
  const [termsChecked, setTermsChecked] = useState(false);

  // State for IMRO Consent
  const [imroConsentChecked, setImroConsentChecked] = useState(false);

  const navigate = useNavigate();

  // Fetch writers to check if this is the first writer
  const writers = useGetMyWriters({ aac: selectedAccount });
  const isFirstWriter = !writers?.data?.writers || writers.data.writers.length === 0;

  // Fetch site config and terms
  const { data: siteConfigData } = useSiteConfigAuth();
  const writerTermsId = siteConfigData?.writer_terms && siteConfigData.writer_terms._id;
  const { data: termsData = null } = useWritersTerms(writerTermsId || "");

  // Use the custom hooks for writer form logic and mutations
  const { form } = useImroWriterForm({ initialData });

  const { createIMROWriter, pending } = useWriterMutation({
    onSuccess: (writerData) => {
      onSuccess?.(writerData);
      onClose();
    },
    onValidationError: (messages) => {
      setValidationErrors(messages);
      setGeneralError(""); // Clear general error when validation errors occur
    },
    onError: (error) => {
      console.error("IMRO Writer creation failed:", error);

      const errorMessage = error.message || "IMRO registration failed. Please try again.";
      setGeneralError(errorMessage);
      setValidationErrors([]);
    },
  });

  const onFormSubmit = async (data) => {
    setValidationErrors([]);
    setGeneralError("");

    try {
      console.log("imro ", data);

      // Send IMRO consent tracking before creating writer

      sendProperty({ property: "imro_data_sharing_consent", value: "CURRENT_DATE", email: data.emailaddress });
      await createIMROWriter(data);
      navigate({ to: "/writers" });
    }
    catch {
      // Error handling is done in the mutation hooks
    }
  };

  return (
    <div className="space-y-4">
      <Heading as="h3">{t("writers:register-new-imro-writer") ?? "IMRO Registration"}</Heading>
      <Text as="p">
        { t("writers:register-new-imro-description") ?? "Register with IMRO (Irish Music Rights Organisation) to get your IPI number. This will allow you to collect royalties for your musical works in Ireland and internationally."}
      </Text>

      {/* Display API validation errors */}
      {validationErrors.length > 0 && (
        <ErrorContainer
          validationIssues={validationErrors}
          title={validationErrors.length === 1 ? String(t("writers:validation-error")) : String(t("writers:validation-errors"))}
          variant="destructive"
          className="mt-4"
        />
      )}

      {/* Display general errors */}
      {generalError && (
        <ErrorContainer
          message={generalError}
          title={String(t("writers:registration-failed") ?? "Registration Failed")}
          variant="destructive"
          className="mt-4"
        />
      )}

      {/* Form */}
      <Form {...form}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void form.handleSubmit(onFormSubmit)(e);
          }}
          className="space-y-4"
        >
          {/* Personal Information */}
          <div>
            <Text>
              Personal Information
            </Text>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstname"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>
                      {t("writers:first-name") ?? "First Name"}
                      {" "}
                      *
                    </FormLabel>
                    <FormControl>
                      <FormInput
                        placeholder={t("writers:first-name") ?? "First Name"}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage>{fieldState?.error?.message}</FormMessage>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastname"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>
                      {t("writers:last-name") ?? "Last Name"}
                      {" "}
                      *
                    </FormLabel>
                    <FormControl>
                      <FormInput
                        placeholder={t("writers:last-name") ?? "Last Name"}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage>{fieldState?.error?.message}</FormMessage>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="dateofbirth"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>
                      {t("writers:date-of-birth") ?? "Date of Birth"}
                      {" "}
                      *
                    </FormLabel>
                    <FormControl>
                      <DatePicker
                        value={field.value instanceof Date ? field.value : undefined}
                        onChange={(date) => {
                          field.onChange(date);
                        }}
                        placeholder="dd/mm/yyyy"
                      />
                    </FormControl>
                    <FormMessage>{fieldState?.error?.message}</FormMessage>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="countryofbirth"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>
                      {t("writers:country-of-birth") ?? "Country of Birth"}
                      {" "}
                      *
                    </FormLabel>
                    <FormControl>
                      <FormInput
                        placeholder={t("writers:country-of-birth") ?? "Country of Birth"}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage>{fieldState?.error?.message}</FormMessage>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="country"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>
                      {t("writers:country") ?? "Country"}
                      {" "}
                      *
                    </FormLabel>
                    <FormControl>
                      <FormInput
                        placeholder={t("writers:country") ?? "Country"}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage>{fieldState?.error?.message}</FormMessage>
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <Text>
              Contact Information
            </Text>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="emailaddress"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>
                      {t("writers:email-address") ?? "Email Address"}
                      {" "}
                      *
                    </FormLabel>
                    <FormControl>
                      <FormInput
                        type="email"
                        placeholder={t("writers:email-address") ?? "your.email@example.com"}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage>{fieldState?.error?.message}</FormMessage>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phonenumber"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>
                      {t("writers:phone-number") ?? "Phone Number"}
                      {" "}
                      *
                    </FormLabel>
                    <FormControl>
                      <FormInput
                        type="tel"
                        placeholder={t("writers:phone-number") ?? "+353 1 234 5678"}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage>{fieldState?.error?.message}</FormMessage>
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Address Information */}
          <div>
            <Text>
              Address Information
            </Text>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="address1"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>
                      {t("writers:address1") ?? "Address Line 1"}
                      {" "}
                      *
                    </FormLabel>
                    <FormControl>
                      <FormInput
                        placeholder={t("writers:address1") ?? "Enter address line 1"}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage>{fieldState?.error?.message}</FormMessage>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address2"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>
                      {t("writers:address2") ?? "Address Line 2"}
                    </FormLabel>
                    <FormControl>
                      <FormInput
                        placeholder={t("writers:address2") ?? "Enter address line 2"}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage>{fieldState?.error?.message}</FormMessage>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address3"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>
                      {t("writers:address3") ?? "Address Line 3"}
                    </FormLabel>
                    <FormControl>
                      <FormInput
                        placeholder={t("writers:address3") ?? "Enter address line 3"}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage>{fieldState?.error?.message}</FormMessage>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address4"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>
                      {t("writers:address4") ?? "Address Line 4"}
                    </FormLabel>
                    <FormControl>
                      <FormInput
                        placeholder={t("writers:address4") ?? "Enter address line 4"}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage>{fieldState?.error?.message}</FormMessage>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address5"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>{t("writers:address5") ?? "Enter Address Line 5"}</FormLabel>
                    <FormControl>
                      <FormInput
                        placeholder={t("writers:address5") ?? "Enter Address Line 5"}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage>{fieldState?.error?.message}</FormMessage>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="taxarea"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>{t("writers:tax-area") ?? "Enter Tax Area"}</FormLabel>
                    <FormControl>
                      <FormInput
                        placeholder={t("writers:tax-area-placeholder") ?? "Enter Tax Area"}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage>{fieldState?.error?.message}</FormMessage>
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Terms & Conditions - only shown if first writer on account */}
          {isFirstWriter && siteConfigData?.show_writer_terms && (
            <div>
              <Text>
                {t("common:terms")}
                {" "}
                &amp;
                {" "}
                {t("common:conditions")}
              </Text>

              <ScrollArea className="h-64 p-4 border rounded-md border-sentric-mid-grey ">
                <div className="pr-4 prose-sm prose dark:prose-invert max-w-none">
                  <PortableText value={termsData?.terms} />
                </div>
              </ScrollArea>

              <div className="mt-4">
                <FormCheckbox
                  id="terms-acceptance"
                  checked={termsChecked}
                  onCheckedChange={setTermsChecked}
                  label={t("writers:i-have-read-terms-and-conditions") || "I have read and agree to the terms and conditions"}
                />
              </div>
            </div>
          )}

          {/* IMRO Consent Checkbox - required */}
          <div className="space-y-2">
            <FormCheckbox
              id="imro-consent"
              checked={imroConsentChecked}
              onCheckedChange={setImroConsentChecked}
              label={t("writers:imro-consent-checkbox") || "I expressly consent to Sentric sharing this information with IMRO for the purpose of becoming a member of IMRO. I understand IMRO will contact me directly utilising the information provided above."}
            />
          </div>

          <DialogFooter className="pt-4">
            <div className="flex gap-2">

              {onBack && (
                <Button
                  variant="outline"
                  label={t("writers:back") ?? "Back"}
                  onClick={onBack}
                  disabled={pending}
                  type="button"
                />
              )}
            </div>
            <Button
              variant={isTunecore ? "primary" : "secondary"}
              label={pending
                ? (t("writers:creating-writer"))
                : t("writers:create-writer")}
              disabled={
                pending
                || !imroConsentChecked // Require IMRO consent
                || (
                  // Disable if terms need to be accepted but haven't been
                  isFirstWriter
                  && siteConfigData?.show_writer_terms === true
                  && termsData != null
                  && !termsChecked
                )
              }
              type="submit"
            />
          </DialogFooter>
        </form>
      </Form>
    </div>
  );
}
